/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { token } from '@atlaskit/tokens';

import {
	SELECTABLE_LOCALES,
	useLocale,
	useSetLocale,
	type LocaleCode,
} from '../../../../../providers/locale-provider';

import { messages } from './messages';

const styles = cssMap({
	trigger: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.050'),
		background: 'transparent',
		border: 'none',
		padding: 0,
		cursor: 'pointer',
		font: token('font.body.small'),
		fontFamily: token('font.family.body'),
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '36px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		alignSelf: 'flex-start',
	},
});

const findNativeName = (code: LocaleCode): string =>
	SELECTABLE_LOCALES.find((l) => l.code === code)?.nativeName ??
	// Fallback: a persisted but non-selectable locale (e.g. en-ZZ pseudo).
	// We still want the trigger to render something readable.
	code;

/**
 * Footer dropdown that lets the user switch the microsite's display
 * language. Reads/writes via the LocaleProvider context — must be
 * mounted inside a <LocaleProvider>.
 */
export const LanguageSwitcher = (): JSX.Element => {
	const { formatMessage } = useIntl();
	const locale = useLocale();
	const setLocale = useSetLocale();
	const activeName = findNativeName(locale);

	return (
		<DropdownMenu
			placement="top-start"
			trigger={({ triggerRef, ...triggerProps }) => (
				<button
					{...triggerProps}
					ref={triggerRef}
					type="button"
					css={styles.trigger}
					aria-label={formatMessage(messages.switcherAriaLabel, { language: activeName })}
				>
					{activeName}
					<ChevronDownIcon label="" />
				</button>
			)}
		>
			<DropdownItemGroup>
				{SELECTABLE_LOCALES.map((descriptor) => (
					<DropdownItem
						key={descriptor.code}
						isSelected={descriptor.code === locale}
						onClick={() => setLocale(descriptor.code)}
					>
						{descriptor.nativeName}
					</DropdownItem>
				))}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
